import Box from '@material-ui/core/Box';
import NyTextInput from 'nyse-web-tools-common/lib/react/form/NyTextInput/NyTextInput';
import AgGrid from 'nyse-web-tools-common/lib/react/grid/AgGrid/AgGrid';
import AgGridColumnMenuTrigger from 'nyse-web-tools-common/lib/react/grid/AgGridColumnMenuTrigger/AgGridColumnMenuTrigger';
import AgGridCreateBtn from 'nyse-web-tools-common/lib/react/grid/AgGridCreateBtn/AgGridCreateBtn';
import AgGridExportMenu from 'nyse-web-tools-common/lib/react/grid/AgGridExportMenu/AgGridExportMenu';
import AgGridPagination from 'nyse-web-tools-common/lib/react/grid/AgGridPagination/AgGridPagination';
import AgGridProvider from 'nyse-web-tools-common/lib/react/grid/AgGridProvider/AgGridProvider';
import AgGridToggleShowFilters from 'nyse-web-tools-common/lib/react/grid/AgGridToggleShowFilters/AgGridToggleShowFilters';
import useManagedState from 'nyse-web-tools-common/lib/react/hooks/useManagedState/useManagedState';
import React, {useMemo} from 'react';

import {Access} from '../components/displays/access';
import {IdbOriginatorModal} from '../components/modals/idbOriginators/idbOriginators.modal';
import {FirmSelect} from '../components/selects/firm.select';
import {IbMpidSelect} from '../components/selects/mpid.select';
import {TradingAccountsSelect} from '../components/selects/tradingAccounts.select';
import {idbOriginatorsColumnDefs} from '../configs/columnDefs/idbOriginators.columnDefs';
import useResource from '../hooks/useResource.hook';
import useRoute from '../hooks/useRoute.hook';
import useStore from '../hooks/useStore.hook';
import idbOrigResource from '../resources/idbOriginators.resource';

export const IdbOriginatorsPage: React.FC = React.memo(() => {
    const route = useRoute();
    const firms = useStore('firms');
    const permissions = useStore('permissions');
    const platform = useStore('platform');
    const resource = useResource(idbOrigResource);

    const init = useMemo(
        () => ({
            pagination: {
                limit: 500,
                offset: 0
            },
            values: {
                activeStatus: true,
                crd: route.params.crd || (firms[0] || {}).crd,
                mpId: ''
            }
        }),
        [firms, route.params.crd]
    );

    const [values, setValues] = useManagedState(init.values);

    return (
        <AgGridProvider
            columnDefs={idbOriginatorsColumnDefs}
            columnNamespace='idbOriginators'
            error={resource.error}
            filtersClassName='row space-bottom-1'
            initialPagination={init.pagination}
            initialFilters={init.values}
            isLoading={resource.pending}
            onFiltersChanged={setValues}
            onLoad={resource.get}
            permissions={permissions}
            primaryKey='idbOriginatorId'
            rowData={resource.data}>
            <AgGridProvider.Filters>
                {firms.length > 1 && (
                    <div className='form-group col-xs-12 col-sm-3 col-md-2 col-lg-2'>
                        <label>IB</label>
                        <FirmSelect
                            name='crd'
                            required
                            resetFields={['mpId', 'tradingAccount']}
                            showInactive
                        />
                    </div>
                )}
                <div className='form-group col-xs-12 col-sm-3 col-md-2 col-lg-2'>
                    <label>MPID</label>
                    <IbMpidSelect
                        name='mpId'
                        crd={values.crd}
                        resetFields={['tradingAccount']}
                    />
                </div>
                <div className='form-group col-xs-12 col-sm-3 col-md-2 col-lg-2'>
                    <label>Trading Account</label>
                    <TradingAccountsSelect
                        name='tradingAccount'
                        crd={values.crd}
                        mpId={values.mpId}
                    />
                </div>
                <div className='form-group col-xs-12 col-sm-3 col-md-2 col-lg-2'>
                    <label>IDB/Originator Id</label>
                    <NyTextInput name='name' maxLength={4} />
                </div>
            </AgGridProvider.Filters>

            <div className='row space-bottom-1'>
                <div className='col-md-12 col-lg-12 col-sm-12 col-xs-12'>
                    <Box display='flex' alignItems='flex-end'>
                        <AgGridProvider.SubmitBtn />
                        <AgGridProvider.ResetBtn />
                        <Box display='flex' ml='auto'>
                            <Access permissions={['create idb-originators']}>
                                <AgGridCreateBtn
                                    Form={IdbOriginatorModal}
                                    initialValueFields={['crd']}
                                    name='createIdbOriginator'
                                    parentTitle='IDB / Originators'
                                    resource={idbOrigResource}
                                    resourceMethod='post'
                                    title='Create'
                                    tooltip='Create'
                                    triggerName='createIdbOriginatorBtn'
                                />
                            </Access>
                            <AgGridToggleShowFilters />
                            <AgGridExportMenu
                                exportFileName='idbOriginators'
                                exportSheetName={platform.name}
                            />
                            <AgGridColumnMenuTrigger />
                        </Box>
                    </Box>
                </div>
            </div>

            <AgGrid enableCellChangeFlash />

            <AgGridPagination>
                <option value={100}>100</option>
                <option value={500}>500</option>
                <option value={1000}>1,000</option>
            </AgGridPagination>
        </AgGridProvider>
    );
});
